import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { gql } from "graphql-tag";
import { toast } from "sonner";

export const DISMISS_EXPERIENCE_MUTATION = gql`
    mutation DismissExperience($messageId: UUID!, $experienceIndex: Int!) {
        messageDismissItem(
            messageId: $messageId
            kind: EXPERIENCE
            targetIndex: $experienceIndex
        )
    }
`;

interface UseExperienceDismissalOptions {
    messageId: string | null | undefined;
    /**
     * Current experienceContext entries on the message. Used to map
     * server-side dismissals (stored as entity UUIDs in dismissed_text) back
     * to current list positions, since positions can shift across regens.
     */
    experienceContext?: ReadonlyArray<{ id: string }> | null;
    /**
     * Server-side EXPERIENCE dismissal records for this message.
     * Each entry's `dismissedText` carries the stable entity UUID.
     */
    serverDismissals?: ReadonlyArray<{ dismissedText?: string | null }> | null;
    /** Called after a successful dismiss; consumers typically refetch. */
    onDismissed?: () => void;
}

export interface UseExperienceDismissalReturn {
    /** 0-based experience-context indices dismissed (server + this session). */
    dismissedExperienceIndices: Set<number>;
    /**
     * Toggle selection for an experience-context entry. Deselect persists via
     * messageDismissItem(kind=EXPERIENCE); re-select is session-local only.
     */
    toggleExperience: (index: number) => void;
    /**
     * Number of user-initiated experience toggles since the current message was loaded.
     * Resets to 0 on messageId change. Use this (not dismissedExperienceIndices.size) for
     * eager-generation triggers so server-seeded initial state never fires a regen.
     */
    userToggleCount: number;
}

/**
 * Persists Erfahrungswissen dismissals via messageDismissItem(kind=EXPERIENCE).
 * Backend resolves the entity UUID from message.experienceContext[index] at
 * dismissal time and stores it on dismissed_item.dismissed_text so regeneration
 * filters by stable identifier.
 *
 * Seeds initial dismissed state from `serverDismissals` so a page reload after
 * dismiss-without-regen does not show items as active that the backend will
 * actually skip on the next generation.
 */
export function useExperienceDismissal({
    messageId,
    experienceContext,
    serverDismissals,
    onDismissed,
}: UseExperienceDismissalOptions): UseExperienceDismissalReturn {
    // Project server dismissals (entity UUIDs) onto current experienceContext positions.
    const seededIndices = useMemo(() => {
        if (!serverDismissals?.length || !experienceContext?.length) return new Set<number>();
        const dismissedUuids = new Set(
            serverDismissals.map((d) => d.dismissedText).filter((t): t is string => !!t),
        );
        const out = new Set<number>();
        experienceContext.forEach((item, idx) => {
            if (dismissedUuids.has(item.id)) out.add(idx);
        });
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageId, serverDismissals, experienceContext]);

    const [dismissedIndices, setDismissedIndices] = useState<Set<number>>(seededIndices);
    const [userToggleCount, setUserToggleCount] = useState(0);

    // Sync state when the message changes (new message = new seed from server).
    useEffect(() => {
        setDismissedIndices(seededIndices);
        setUserToggleCount(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageId]);

    const inFlightRef = useRef(new Set<number>());

    const [dismissMutation] = useMutation<{ messageDismissItem: boolean }>(
        DISMISS_EXPERIENCE_MUTATION,
        {
            onError: (error) =>
                toast.error(`Erfahrungswissen konnte nicht verworfen werden: ${error.message}`),
            onCompleted: () => onDismissed?.(),
        },
    );

    const toggleExperience = (index: number) => {
        if (!messageId) return;
        setUserToggleCount((c) => c + 1);
        if (dismissedIndices.has(index)) {
            setDismissedIndices((prev) => {
                const next = new Set(prev);
                next.delete(index);
                return next;
            });
        } else {
            if (inFlightRef.current.has(index)) return;
            inFlightRef.current.add(index);
            setDismissedIndices((prev) => {
                const next = new Set(prev);
                next.add(index);
                return next;
            });
            void dismissMutation({ variables: { messageId, experienceIndex: index } }).finally(
                () => inFlightRef.current.delete(index),
            );
        }
    };

    return { dismissedExperienceIndices: dismissedIndices, toggleExperience, userToggleCount };
}
